<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {useCompanyStore} from '../../stores/company'
import {useGlobalStore} from '../../stores/global'
import {useNotificationsStore} from '../../stores/notifications'
import {useRefreshNotifications} from '../../composable/useRefreshNotifications'
import {useWindowSize} from '../../composable/useWindowSize'
import {getEnvironment} from '../../utils/getEnvironment'
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import AccountDropdown from './AccountDropdown.vue'
import BurgerMenu from './BurgerMenu.vue'
import CompanyButton from './CompanyButton.vue'
import HorizontalMenu from './HorizontalMenu.vue'
import NotificationBanner from '../notification/NotificationBanner.vue'
import NotificationBell from '../notification/NotificationBell.vue'
import SystemAlertSidePanel from '../SystemAlertSidePanel.vue'
import KnowledgeBaseLink from '@/components/header/KnowledgeBaseLink.vue'
import {useAssistantStore} from '@/stores/assistant'
import AssistantIcon from '@/components/assistant/AssistantIcon.vue'

const assistantStore = useAssistantStore()
const companyStore = useCompanyStore()
const globalStore = useGlobalStore()
const notificationsStore = useNotificationsStore()
const isNewUiEnabled = useNewUiEnabled()

useRefreshNotifications()

const showBurgerMenu = ref(false)

const [windowSize] = useWindowSize()
const isBurgerMenu = computed(() => windowSize.value < 1200)
const isSidebarBurgerMenu = computed(() => windowSize.value > 600)

const headerIconClasses = computed(() => ({
  grow: isBurgerMenu.value,
}))

const showMenuItems = computed(() => {
  if (globalStore.hasAuthority('ADMIN', 'PMS_ADMIN')) return true
  return !!companyStore.dpaAccepted
})

watch(isBurgerMenu, (newVal) => {
  if (newVal === false) showBurgerMenu.value = false
})

function onToggleMenu() {
  showBurgerMenu.value = !showBurgerMenu.value
}

function closeSidePanel() {
  notificationsStore.showSidePanel = false
}
</script>

<template>
  <header
    :class="{
      'w-full': true,
      'bg-new-primary-default': isNewUiEnabled && !globalStore.isSuperAdmin,
      'bg-branddark': !isNewUiEnabled && !globalStore.isSuperAdmin,
      'bg-red': globalStore.isSuperAdmin && getEnvironment() === 'production',
      'bg-devgreen': globalStore.isSuperAdmin && getEnvironment() === 'development',
      'bg-stagingblue': globalStore.isSuperAdmin && getEnvironment() === 'staging',
      'bg-localgrey': globalStore.isSuperAdmin && getEnvironment() === 'localhost',
    }"
  >
    <div class="w-full bg-inherit" :class="{'max-w-screen-new-lg mx-auto': isNewUiEnabled}">
      <div
        class="relative h-20 flex flex-row gap-x-5 items-center bg-inherit z-header"
        :class="isNewUiEnabled ? 'px-new-lg new-sm:px-new-4xl' : 'px-5'"
      >
        <!-- Header icon -->
        <div :class="headerIconClasses">
          <a href="/" @click="closeSidePanel">
            <img alt="RateBoard" class="float-left w-32 mx-auto hover:scale-105 duration-300" src="/logo_home.png" title="Home" />
          </a>
        </div>

        <!-- Header menu (LARGE) -->
        <HorizontalMenu
          v-if="!isBurgerMenu"
          class="flex-grow"
          :class="{'ml-7': isNewUiEnabled}"
          :show-menu-items="showMenuItems"
          @toggle-menu="closeSidePanel"
        ></HorizontalMenu>

        <div class="flex flex-row items-center min-w-fit" :class="[isNewUiEnabled ? 'gap-x-5' : 'gap-x-3']">
          <button @click="assistantStore.toggleVisible">
            <AssistantIcon></AssistantIcon>
          </button>

          <KnowledgeBaseLink />
          <!-- Notification bell -->
          <NotificationBell v-if="globalStore.hasAuthority('HOTEL_ADMIN')"></NotificationBell>

          <!-- Account dropdown menu or company button -->
          <AccountDropdown v-if="!isBurgerMenu" @toggle-menu="onToggleMenu"></AccountDropdown>
          <CompanyButton v-else-if="isSidebarBurgerMenu" class="h-20 max-w-[350px]" @click="onToggleMenu"></CompanyButton>

          <!-- Burger menu -->
          <button
            v-if="isBurgerMenu"
            class="text-white leading-none outline-none focus:outline-none"
            :class="[isNewUiEnabled ? 'p-1' : 'px-3 py-1']"
            type="button"
            @click="onToggleMenu"
          >
            <i
              class="fas fa-bars hover:scale-125 duration-200"
              :class="[isNewUiEnabled ? 'text-new-lg inline-block w-6 text-center' : 'text-4xl', {hidden: showBurgerMenu}]"
            ></i>
            <i
              v-if="isSidebarBurgerMenu"
              class="fas fa-xmark"
              :class="[
                isNewUiEnabled ? 'text-new-lg inline-block w-6 text-center' : 'text-5xl hover:scale-125 duration-200',
                {hidden: !showBurgerMenu},
              ]"
            ></i>
          </button>
        </div>

        <!-- Header menu (BURGER) -->
        <template v-if="isBurgerMenu">
          <BurgerMenu
            :class="
              isSidebarBurgerMenu
                ? {
                    'absolute top-20 right-0 w-fit overflow-y-scroll bg-inherit': true,
                    'px-new-3xl': isNewUiEnabled,
                    'px-5': !isNewUiEnabled,
                    'h-0 duration-300': !showBurgerMenu,
                    'h-[calc(100vh-5rem)] pt-new-3xl pb-new-3xl duration-500': showBurgerMenu && isNewUiEnabled,
                    'h-[calc(100vh-5rem)] pt-5 pb-5 duration-500': showBurgerMenu && !isNewUiEnabled,
                  }
                : {
                    'absolute top-0 left-0 w-full overflow-y-scroll bg-inherit': true,
                    'h-0 duration-300': !showBurgerMenu,
                    'h-screen duration-500': showBurgerMenu && isNewUiEnabled,
                    'h-screen pt-20 pb-20 duration-500': showBurgerMenu && !isNewUiEnabled,
                  }
            "
            :show-menu="showBurgerMenu"
            :show-menu-items="showMenuItems"
            :sidebar="isSidebarBurgerMenu"
            @toggle-menu="onToggleMenu"
          >
          </BurgerMenu>

          <button
            v-if="!isSidebarBurgerMenu && showBurgerMenu"
            class="leading-none outline-none focus:outline-none z-header"
            :class="isNewUiEnabled ? 'absolute top-new-2xl right-4 p-0 text-new-neutral-0' : 'text-white px-3 py-1'"
            type="button"
            @click="onToggleMenu"
          >
            <i
              class="fas fa-xmark"
              :class="isNewUiEnabled ? 'text-[24px] inline-block w-6 text-center' : 'text-5xl hover:scale-125 duration-200'"
            ></i>
          </button>
        </template>
      </div>
    </div>
  </header>

  <SystemAlertSidePanel />

  <div class="absolute top-[6rem] right-8 hidden md:block" :class="{'drop-shadow-lg': !isNewUiEnabled}">
    <NotificationBanner />
  </div>
</template>
